import { forwardRef, useEffect, useRef, useImperativeHandle, useCallback } from 'react';
import { Platform } from 'react-native';
import {
  YoutubeView,
  useYouTubePlayer,
  useYouTubeEvent,
  PlayerState,
  YoutubePlayer,
} from 'react-native-youtube-bridge';
import { View, YStack, Image, VideoPlayIconRegular, useTheme } from '@ninjacart/nc-nk-ui';
import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  handleMarketingEvents,
  KissanEventType,
  msToTime,
  sendToFirebasEvents,
} from '@ninjacart/nc-nk-store';
import { extractVideoId, YoutubeBridgePlayerProps } from '@ninjacart/nc-nk-core';
import { useSearchParams } from 'solito/navigation';

export interface YoutubeBridgePlayerHandle {
  toggleMute: (muted?: boolean) => void;
  isMuted: () => boolean;
  setVolume: (volume: number) => void;
}

export const YoutubeBridgePlayer = forwardRef<YoutubeBridgePlayerHandle, YoutubeBridgePlayerProps>(({
  url,
  isPlaying = false,
  showControls = false,
  autoPlay = false,
  screenName,
  isAutoplayEvent,
  index,
  muted = false,
  onMuteStateChange,
  style,
  eventId = '',
  recommendationId = '',
  rankingId = '',
  videoId = '',
}, ref) => {
  const searchParams = useSearchParams();
  const itemIndex = searchParams?.get('itemIndex');
  const theme = useTheme();
  const processEvents = useRef(useQueuedEventStore((state) => state.processEvents)).current;

  // ✅ Track mute state internally
  const isMutedRef = useRef(muted);
  const playerRef = useRef<YoutubePlayer | null>(null);
  const youtubeVideoId = extractVideoId(url ?? '');
  
  if (!youtubeVideoId) {
    console.error('❌ No YouTube video ID found in URL:', url);
    return null;
  }
  
  // ✅ Use props from navigation params as fallback
  const effectiveEventId = eventId || searchParams?.get('eventId') || '';
  const effectiveRecommendationId = recommendationId || searchParams?.get('recommendationId') || '';
  const effectiveRankingId = rankingId || searchParams?.get('rankingId') || '';
  const effectiveVideoId = videoId || searchParams?.get('videoId') || '';
  const effectiveItemIndex = itemIndex || String(index) || '0';
  const effectiveScreenName = screenName || searchParams?.get('screenName') || 'feed';
  const effectiveIsAutoplayEvent = isAutoplayEvent || searchParams?.get('isAutoplayEvent') === 'true';

  const player = useYouTubePlayer(youtubeVideoId, {
    autoplay: autoPlay,
    controls: true,
    playsinline: true,
    rel: false,
    muted: Platform.OS === 'web' ? true : muted, // Initial mute state
  });

  playerRef.current = player as any;

  // ✅ FIXED: Proper mute functionality for both web and mobile
  const toggleMuteInternal = useCallback((mutedState?: boolean) => {
    const newMuteState = mutedState !== undefined ? mutedState : !isMutedRef.current;
    isMutedRef.current = newMuteState;
    if (Platform.OS === 'web') {
      // Web implementation
      const webPlayer = playerRef.current as any;
      if (webPlayer) {
        try {
          if (newMuteState) {
            if (typeof webPlayer.mute === 'function') {
              webPlayer.mute();
            }
          } else {
            if (typeof webPlayer.unMute === 'function') {
              webPlayer.unMute();
            }
          }
        } catch (error) {
          console.error('Error toggling YouTube mute (Web):', error);
        }
      }
    } else {
      // Mobile implementation
      const mobilePlayer = playerRef.current;
      if (mobilePlayer) {
        try {
          // For mobile, we need to use the player methods directly
          if (newMuteState) {
            // Mute the video
            if (typeof (mobilePlayer as any).mute === 'function') {
              (mobilePlayer as any).mute();
            }
          } else {
            // Unmute the video
            if (typeof (mobilePlayer as any).unMute === 'function') {
              (mobilePlayer as any).unMute();
            }
          }
        } catch (error) {
          console.error('Error toggling YouTube mute (Mobile):', error);
        }
      }
    }
    
    // Notify parent of mute state change
    onMuteStateChange?.(newMuteState);
    return newMuteState;
  }, [onMuteStateChange]);

  // ✅ Expose methods to parent component
  useImperativeHandle(ref, () => ({
    toggleMute: toggleMuteInternal,
    isMuted: () => isMutedRef.current,
    setVolume: (volume: number) => {
      if (playerRef.current) {
        try {
          if (typeof (playerRef.current as any).setVolume === 'function') {
            (playerRef.current as any).setVolume(volume);
          }
        } catch (error) {
          console.error('Error setting volume:', error);
        }
      }
    },
  }));

  const hasTriggeredReadyEvent = useRef(false);
  const isPlayerReadyRef = useRef(false);
  const startTimeRef = useRef<number>(0);
  const currentTimeRef = useRef(0);
  const hasEnded = useRef(false);
  const lastEventTime = useRef(0);
  const milestoneFlags = useRef({
    fiveSec: false,
    tenSec: false,
    twentySec: false,
    thirtySec: false,
    postAutoplay: false,
  });
  const previousState = useRef<PlayerState | null>(null);

  // ✅ FIXED: Apply initial mute state when player is ready and when props change
  useEffect(() => {
    if (playerRef.current && isPlayerReadyRef.current) {
      toggleMuteInternal(muted);
    }
  }, [muted, isPlayerReadyRef.current, toggleMuteInternal]);

  // Helper: enqueue event
  const enqueueEvent = (
    eventName: KissanEventType,
    additionalParams: { key: string; value: any }[] = [],
    customScreenDetails?: { screen?: string; screenTimeInSecs?: number }
  ) => {
    // ✅ USE EFFECTIVE VALUES (props with URL params as fallback)
    const commonDetails = [
      addEventParam('videoId', effectiveVideoId),
      addEventParam('deeplinkingUrl', `ninjakisan://longVideo/${effectiveVideoId}?type=STREAM`),
      addEventParam('recommendationId', effectiveRecommendationId),
      addEventParam('eventId', effectiveEventId),
      addEventParam('itemIndex', effectiveItemIndex),
    ];

    if (effectiveRankingId) {
      commonDetails.push(
        addEventParam('referenceKey4', 'rankingId'),
        addEventParam('referenceValue4', effectiveRankingId)
      );
    }

    const additionalDetails = [...commonDetails, ...additionalParams];

    const screenDetails = { 
      screen: 'feed video youtube player', 
      ...customScreenDetails 
    };

    const event: INinjaEvent = {
      name: eventName,
      screenDetails,
      eventParams: additionalDetails,
    };
    processEvents([event]);
    
    if (sendToFirebasEvents.includes(eventName)) {
      handleMarketingEvents(eventName, { screen: effectiveScreenName }, event.eventParams);
    }
  };

  // Safe play / pause for web + native
  const playVideo = () => {
    if (Platform.OS === 'web') {
      const webPlayer = player as any;
      if (!isPlayerReadyRef.current || !webPlayer) return;
      if (typeof webPlayer.playVideo === 'function') {
        try {
          webPlayer.playVideo();
        } catch {}
      } else if (typeof webPlayer.play === 'function') {
        try {
          webPlayer.play();
        } catch {}
      }
    } else {
      playerRef.current?.play?.();
    }
  };

  const pauseVideo = () => {
    if (Platform.OS === 'web') {
      const webPlayer = player as any;
      if (!isPlayerReadyRef.current || !webPlayer) return;
      if (typeof webPlayer.stopVideo === 'function') {
        try {
          webPlayer.stopVideo();
        } catch {}
      } else if (typeof webPlayer.pauseVideo === 'function') {
        try {
          webPlayer.pauseVideo();
        } catch {}
      } else if (typeof webPlayer.pause === 'function') {
        try {
          webPlayer.pause();
        } catch {}
      }
    } else {
      playerRef.current?.pause?.();
    }
  };

  // Handle playing / pausing based on `isPlaying`
  useEffect(() => {
    if (isPlaying) {
      hasTriggeredReadyEvent.current = false;
      startTimeRef.current = performance.now();
      currentTimeRef.current = 0;
      hasEnded.current = false;
      milestoneFlags.current = {
        fiveSec: false,
        tenSec: false,
        twentySec: false,
        thirtySec: false,
        postAutoplay: false,
      };
      playVideo();
    } else {
      pauseVideo();
    }
  }, [isPlaying, youtubeVideoId, effectiveVideoId]);

  // Ready event
  useYouTubeEvent(player, 'ready', () => {
    if (hasTriggeredReadyEvent.current) return;
    isPlayerReadyRef.current = true;
    
    // ✅ Apply mute state when player is ready
    toggleMuteInternal(muted);
    
    if (isPlaying || autoPlay) startTimeRef.current = performance.now();
    
    enqueueEvent(
      effectiveIsAutoplayEvent
        ? KissanEventType.AUTO_PLAY_VIDEO_ON_READY_TIME
        : KissanEventType.VIDEO_ON_READY_TIME
    );
    
    hasTriggeredReadyEvent.current = true;

    // Web autoplay workaround
    if (Platform.OS === 'web' && (isPlaying || autoPlay)) {
      const webPlayer = player as any;
      try {
        if (typeof webPlayer?.playVideo === 'function') webPlayer.playVideo();
        else if (typeof webPlayer?.play === 'function') webPlayer.play();
        setTimeout(() => {
          try {
            // Ensure unmute works after autoplay
            if (typeof webPlayer?.unMute === 'function' && !muted) {
              webPlayer.unMute();
            }
          } catch {}
        }, 500);
      } catch {}
    }
  });

  // State changes: PLAY, PAUSE, BUFFERING, END
  const state = useYouTubeEvent(player, 'stateChange');
  useEffect(() => {
    if (state == null) return;
    const numericState = state as unknown as number;

    switch (numericState) {
      case PlayerState.PLAYING:
        const bufferEndTime = performance.now();
        enqueueEvent(
          effectiveIsAutoplayEvent ? KissanEventType.AUTO_PLAY_VIDEO_PLAY : KissanEventType.VIDEO_PLAY
        );
        const calculatedBufferTime = msToTime(bufferEndTime - startTimeRef.current);
        if (calculatedBufferTime >= 0) {
          bufferTimeCapture(calculatedBufferTime);
        }
        break;
      case PlayerState.PAUSED:
        enqueueEvent(KissanEventType.VIDEO_PAUSED);
        break;
      case PlayerState.BUFFERING:
        startTimeRef.current = performance.now();
        break;
      case PlayerState.ENDED:
        if (!hasEnded.current) {
          hasEnded.current = true;
          const endTime = performance.now();
          const screenTiming = msToTime(endTime - startTimeRef.current);
          enqueueEvent(
            effectiveIsAutoplayEvent
              ? KissanEventType.AUTO_PLAY_VIDEO_PLAY_FINISH
              : KissanEventType.VIDEO_PLAY_FINISH,
            [addEventParam('videoPlaybackTime', currentTimeRef.current)],
            { screenTimeInSecs: screenTiming }
          );
        }
        break;
    }
  }, [state]);

  const bufferTimeCapture = (calculatedBufferTime: number) => {
    enqueueEvent(
      effectiveIsAutoplayEvent
        ? KissanEventType.AUTO_PLAY_VIDEO_BUFFER_TIME
        : KissanEventType.VIDEO_BUFFER_TIME,
      [addEventParam('bufferTime', String(calculatedBufferTime))] 
    );
  };

  // Progress tracking
  const progress = useYouTubeEvent(player, 'progress', 1000);
  useEffect(() => {
    if (!progress) return;
    const currentTime = Math.floor(progress.currentTime || 0);
    currentTimeRef.current = currentTime;

    // Non-autoplay: fire <5s event
    if (currentTime === 5 && !milestoneFlags.current.fiveSec && !effectiveIsAutoplayEvent) {
      enqueueEvent(KissanEventType.VIDEO_VIEWED_LESS_THAN_FIVE_SECONDS, [
        addEventParam('videoPlaybackTime', currentTime),
      ]);
      milestoneFlags.current.fiveSec = true;
    }

    // Autoplay: fire 10s / 20s / 30s milestones
    if (effectiveIsAutoplayEvent) {
      if (currentTime >= 10 && !milestoneFlags.current.tenSec) {
        enqueueEvent(KissanEventType.AUTO_VIDEO_PLAY_VIDEO_PROGRESS_ONE, [
          addEventParam('videoPlaybackTime', currentTime),
        ]);
        milestoneFlags.current.tenSec = true;
      }
      if (currentTime >= 20 && !milestoneFlags.current.twentySec) {
        enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_PROGRESS_TWENTY_SECONDS, [
          addEventParam('videoPlaybackTime', currentTime),
        ]);
        milestoneFlags.current.twentySec = true;
      }
      if (currentTime >= 30 && !milestoneFlags.current.thirtySec) {
        enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_PROGRESS_THIRTY_SECONDS, [
          addEventParam('videoPlaybackTime', currentTime),
        ]);
        milestoneFlags.current.thirtySec = true;
      }
    }

    // Every 10 seconds progress event
    if (currentTime - lastEventTime.current >= 10) {
      enqueueEvent(
        effectiveIsAutoplayEvent
          ? KissanEventType.AUTO_VIDEO_PLAY_VIDEO_PROGRESS
          : KissanEventType.VIDEO_PROGRESS,
        [addEventParam('videoPlaybackTime', currentTime)]
      );
      lastEventTime.current = currentTime;
    }

    // End event
    if (progress.duration && currentTime >= progress.duration - 0.2) {
      if (!hasEnded.current) {
        hasEnded.current = true;
        const screenTiming = msToTime(progress.duration);
        enqueueEvent(
          effectiveIsAutoplayEvent
            ? KissanEventType.AUTO_PLAY_VIDEO_PLAY_FINISH
            : KissanEventType.VIDEO_PLAY_FINISH,
          [addEventParam('videoPlaybackTime', currentTime)],
          { screenTimeInSecs: screenTiming }
        );
      }
    }
  }, [progress]);

  return (
    <View overflow="hidden" width={'100%'} style={style}>
      <YoutubeView
        useInlineHtml={true}
        player={player}
        webViewProps={{ renderToHardwareTextureAndroid: true }}
        style={{ height: 240, width: '100%' }}
      />

      {!isPlaying && !showControls && (
        <YStack width="100%" height={240} position="relative">
          <Image
            source={{ uri: `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg` }}
            objectFit="cover"
            width="100%"
            height={240}
          />
          <YStack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            backgroundColor="rgba(0,0,0,0.4)"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
          >
            <VideoPlayIconRegular size={50} color={theme['background']?.val} />
          </YStack>
        </YStack>
      )}
    </View>
  );
});

YoutubeBridgePlayer.displayName = 'YoutubeBridgePlayer';